// src/utils/dayjsCustomLocales.js
// *******************************************************************************
// CUSTOM DAYJS LOCALES
// *******************************************************************************
// Locales customizados que separam LANGUAGE (idioma das palavras) de
// LOCALE (formato de data/número). Permite ter palavras em inglês mas
// formato de data brasileiro, por exemplo.
// *******************************************************************************

import dayjs from 'dayjs';

// *******************************************************************************
// CUSTOM LOCALE: pt-br (Portuguese with 24h time format)
// *******************************************************************************
// Português brasileiro com formato 24h (sobrescreve o padrão que usa 12h AM/PM)
const ptBrLocale = {
  name: 'pt-br',
  weekdays: 'domingo_segunda-feira_terça-feira_quarta-feira_quinta-feira_sexta-feira_sábado'.split(
    '_'
  ),
  weekdaysShort: 'dom_seg_ter_qua_qui_sex_sáb'.split('_'),
  weekdaysMin: 'Do_2ª_3ª_4ª_5ª_6ª_Sá'.split('_'),
  months:
    'janeiro_fevereiro_março_abril_maio_junho_julho_agosto_setembro_outubro_novembro_dezembro'.split(
      '_'
    ),
  monthsShort: 'jan_fev_mar_abr_mai_jun_jul_ago_set_out_nov_dez'.split('_'),
  ordinal: n => `${n}º`,
  formats: {
    LT: 'HH:mm', // 24h format
    LTS: 'HH:mm:ss', // 24h format
    L: 'DD/MM/YYYY',
    LL: 'D [de] MMMM [de] YYYY',
    LLL: 'D [de] MMMM [de] YYYY [às] HH:mm', // 24h format
    LLLL: 'dddd, D [de] MMMM [de] YYYY [às] HH:mm', // 24h format
  },
  relativeTime: {
    future: 'em %s',
    past: 'há %s',
    s: 'poucos segundos',
    m: 'um minuto',
    mm: '%d minutos',
    h: 'uma hora',
    hh: '%d horas',
    d: 'um dia',
    dd: '%d dias',
    M: 'um mês',
    MM: '%d meses',
    y: 'um ano',
    yy: '%d anos',
  },
};

// *******************************************************************************
// CUSTOM LOCALE: en-br (English words + Brazilian format)
// *******************************************************************************
// Inglês com formato brasileiro (DD/MM/YYYY)
const enBrLocale = {
  name: 'en-br',
  weekdays: 'Sunday_Monday_Tuesday_Wednesday_Thursday_Friday_Saturday'.split('_'),
  weekdaysShort: 'Sun_Mon_Tue_Wed_Thu_Fri_Sat'.split('_'),
  weekdaysMin: 'Su_Mo_Tu_We_Th_Fr_Sa'.split('_'),
  months:
    'January_February_March_April_May_June_July_August_September_October_November_December'.split(
      '_'
    ),
  monthsShort: 'Jan_Feb_Mar_Apr_May_Jun_Jul_Aug_Sep_Oct_Nov_Dec'.split('_'),
  ordinal: n => {
    const s = ['th', 'st', 'nd', 'rd'];
    const v = n % 100;
    return `[${n}${s[(v - 20) % 10] || s[v] || s[0]}]`;
  },
  formats: {
    LT: 'HH:mm',
    LTS: 'HH:mm:ss',
    L: 'DD/MM/YYYY', // Brazilian format
    LL: 'D MMMM YYYY',
    LLL: 'D MMMM YYYY HH:mm',
    LLLL: 'dddd, D MMMM YYYY HH:mm',
  },
  relativeTime: {
    future: 'in %s',
    past: '%s ago',
    s: 'a few seconds',
    m: 'a minute',
    mm: '%d minutes',
    h: 'an hour',
    hh: '%d hours',
    d: 'a day',
    dd: '%d days',
    M: 'a month',
    MM: '%d months',
    y: 'a year',
    yy: '%d years',
  },
};

// *******************************************************************************
// CUSTOM LOCALE: pt-br-us (Portuguese words + US format)
// *******************************************************************************
// Português com formato americano (MM/DD/YYYY) - caso alguém precise
const ptBrUsLocale = {
  name: 'pt-br-us',
  weekdays: 'domingo_segunda-feira_terça-feira_quarta-feira_quinta-feira_sexta-feira_sábado'.split(
    '_'
  ),
  weekdaysShort: 'dom_seg_ter_qua_qui_sex_sáb'.split('_'),
  weekdaysMin: 'Do_2ª_3ª_4ª_5ª_6ª_Sá'.split('_'),
  months:
    'janeiro_fevereiro_março_abril_maio_junho_julho_agosto_setembro_outubro_novembro_dezembro'.split(
      '_'
    ),
  monthsShort: 'jan_fev_mar_abr_mai_jun_jul_ago_set_out_nov_dez'.split('_'),
  ordinal: n => `${n}º`,
  formats: {
    LT: 'HH:mm',
    LTS: 'HH:mm:ss',
    L: 'MM/DD/YYYY', // US format
    LL: 'D [de] MMMM [de] YYYY',
    LLL: 'D [de] MMMM [de] YYYY [às] HH:mm',
    LLLL: 'dddd, D [de] MMMM [de] YYYY [às] HH:mm',
  },
  relativeTime: {
    future: 'em %s',
    past: 'há %s',
    s: 'poucos segundos',
    m: 'um minuto',
    mm: '%d minutos',
    h: 'uma hora',
    hh: '%d horas',
    d: 'um dia',
    dd: '%d dias',
    M: 'um mês',
    MM: '%d meses',
    y: 'um ano',
    yy: '%d anos',
  },
};

// *******************************************************************************
// REGISTER CUSTOM LOCALES
// *******************************************************************************
// Function to ensure custom locales are registered
export function registerCustomLocales() {
  // Register pt-br locale with 24h time format (overrides default)
  dayjs.locale('pt-br', ptBrLocale);

  // Register en-br locale (English with Brazilian date format)
  if (!dayjs.Ls['en-br']) {
    dayjs.locale('en-br', enBrLocale);
  }

  // Register pt-br-us locale (Portuguese with US date format)
  if (!dayjs.Ls['pt-br-us']) {
    dayjs.locale('pt-br-us', ptBrUsLocale);
  }
}

// NOTE: Custom locales are registered in ConfigIndex.js AFTER dayjs plugins are loaded
// This ensures localizedFormat plugin respects our custom formats

// *******************************************************************************
// HELPER: Get dayjs locale based on language and preferred date format
// *******************************************************************************
/**
 * Retorna o locale correto do dayjs baseado em language e formato de data preferido
 *
 * @param {string} language - Idioma ('pt-br', 'en-us')
 * @param {string} dateFormat - Formato de data preferido ('BR', 'US', 'auto')
 * @returns {string} Locale do dayjs a ser usado
 *
 * @example
 * // Inglês com formato brasileiro
 * getDayjsLocale('en-us', 'BR') // 'en-br'
 *
 * // Português com formato brasileiro (padrão)
 * getDayjsLocale('pt-br', 'BR') // 'pt-br'
 *
 * // Inglês com formato americano (padrão)
 * getDayjsLocale('en-us', 'US') // 'en'
 */
export function getDayjsLocale(language, dateFormat = 'auto') {
  const lang = (language || 'en-us').toLowerCase();
  const format = (dateFormat || 'auto').toLowerCase(); // Convert to lowercase

  // Auto: usa formato baseado no language
  if (format === 'auto') {
    if (lang.startsWith('pt')) return 'pt-br';
    if (lang.startsWith('en')) return 'en';
    return 'en';
  }

  // Formato explícito - now comparing lowercase
  if (format === 'br') {
    if (lang.startsWith('pt')) return 'pt-br';
    if (lang.startsWith('en')) return 'en-br'; // Custom locale!
    return 'en-br';
  }

  if (format === 'us') {
    if (lang.startsWith('pt')) return 'pt-br-us'; // Custom locale!
    if (lang.startsWith('en')) return 'en';
    return 'en';
  }

  // Fallback
  return lang.startsWith('pt') ? 'pt-br' : 'en';
}

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default {
  getDayjsLocale,
  ptBrLocale,
  enBrLocale,
  ptBrUsLocale,
};
